// src/diag/renderDiag.ts
// DEV-only render diagnostics for debugging render loops
// Toggle via: localStorage.setItem('vb_debug_render', '1')

const isEnabled = () =>
  import.meta.env.DEV &&
  typeof localStorage !== 'undefined' &&
  localStorage.getItem('vb_debug_render') === '1';

// Per-component render counters
const renderCounts = new Map<string, number>();
// Per-component previous props
const prevProps = new Map<string, Record<string, unknown>>();
// Effect trigger tracking
const effectCounts = new Map<string, number>();

type PropValue = unknown;

function formatValue(val: PropValue, maxLen = 60): string {
  if (val === undefined) return 'undefined';
  if (val === null) return 'null';
  if (typeof val === 'function') return `[fn:${val.name || 'anonymous'}]`;
  if (Array.isArray(val)) return `Array(${val.length})`;
  if (typeof val === 'object') {
    try {
      const str = JSON.stringify(val);
      return str.length > maxLen ? str.slice(0, maxLen) + '...' : str;
    } catch {
      return '[object]';
    }
  }
  const str = String(val);
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str;
}

function shallowEqual(a: PropValue, b: PropValue): boolean {
  if (a === b) return true;
  if (typeof a === 'function' && typeof b === 'function') {
    // Functions are considered "changed" if they're different references
    return a === b;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    // For arrays, check reference only (shallow)
    return a === b;
  }
  return false;
}

export interface TrackedProps {
  selectedIds?: string[];
  zoom?: number;
  scale?: number;
  onStageMouseDown?: unknown;
  onStageMouseMove?: unknown;
  onStageMouseUp?: unknown;
  onNodeDragStart?: unknown;
  onNodeDragMove?: unknown;
  onNodeDragEnd?: unknown;
  onNodeTransformEnd?: unknown;
  onSelectElement?: unknown;
  onZoom?: unknown;
  updateEl?: unknown;
  onContextMenu?: unknown;
  onMotionPathChange?: unknown;
  onExitMotionPathEdit?: unknown;
  elements?: unknown[];
  stagePos?: { x: number; y: number };
  playhead?: number;
  [key: string]: unknown;
}

/**
 * Call at top of component to log render info.
 * Returns render count for this component.
 */
export function logRender(componentName: string, props: TrackedProps): number {
  if (!isEnabled()) return 0;

  const count = (renderCounts.get(componentName) || 0) + 1;
  renderCounts.set(componentName, count);

  const prev = prevProps.get(componentName) || {};
  const changedKeys: string[] = [];
  const changes: Array<{ key: string; prev: PropValue; next: PropValue }> = [];

  // Props we specifically care about
  const trackKeys = [
    'selectedIds',
    'zoom',
    'scale',
    'onStageMouseDown',
    'onStageMouseMove',
    'onStageMouseUp',
    'onNodeDragStart',
    'onNodeDragMove',
    'onNodeDragEnd',
    'onNodeTransformEnd',
    'onSelectElement',
    'onZoom',
    'updateEl',
    'onContextMenu',
    'onMotionPathChange',
    'onExitMotionPathEdit',
    'elements',
    'stagePos',
    'playhead',
  ];

  for (const key of trackKeys) {
    if (!(key in props)) continue;
    const prevVal = prev[key];
    const nextVal = props[key];

    if (!shallowEqual(prevVal, nextVal)) {
      changedKeys.push(key);
      changes.push({ key, prev: prevVal, next: nextVal });
    }
  }

  // Store current props for next render
  const snapshot: Record<string, unknown> = {};
  for (const key of trackKeys) {
    if (key in props) {
      snapshot[key] = props[key];
    }
  }
  prevProps.set(componentName, snapshot);

  // Log grouped output
  const label = `🔄 ${componentName} render #${count}${changedKeys.length > 0 ? ` [${changedKeys.join(', ')}]` : ' [no tracked changes]'}`;
  console.groupCollapsed(label);
  console.log('Render #:', count);
  console.log('Changed keys:', changedKeys.length > 0 ? changedKeys : '(none tracked)');

  if (changes.length > 0) {
    console.group('Changes:');
    for (const { key, prev: p, next: n } of changes) {
      console.log(`  ${key}:`);
      console.log(`    prev: ${formatValue(p)}`);
      console.log(`    next: ${formatValue(n)}`);
    }
    console.groupEnd();
  }

  console.groupEnd();

  return count;
}

/**
 * Call inside useEffect to log when it runs and what triggered it.
 * @param componentName - Name of component
 * @param effectName - Descriptive name for the effect
 * @param deps - The dependency array values
 * @param depNames - Names/descriptions for each dep
 */
export function logEffect(
  componentName: string,
  effectName: string,
  deps: unknown[],
  depNames: string[]
): void {
  if (!isEnabled()) return;

  const effectKey = `${componentName}:${effectName}`;
  const count = (effectCounts.get(effectKey) || 0) + 1;
  effectCounts.set(effectKey, count);

  const depsSnapshot = deps.map((d, i) => ({
    name: depNames[i] || `dep[${i}]`,
    value: d,
  }));

  console.groupCollapsed(`⚡ ${componentName}::${effectName} run #${count}`);
  console.log('Effect run #:', count);
  console.log('Dependencies snapshot:');
  for (const { name, value } of depsSnapshot) {
    console.log(`  ${name}: ${formatValue(value)}`);
  }
  console.groupEnd();
}

/**
 * Reset all counters (useful for testing)
 */
export function resetDiag(): void {
  renderCounts.clear();
  prevProps.clear();
  effectCounts.clear();
  console.log('🔄 Render diagnostics reset');
}

/**
 * Get current stats
 */
export function getDiagStats(): {
  renders: Record<string, number>;
  effects: Record<string, number>;
} {
  return {
    renders: Object.fromEntries(renderCounts),
    effects: Object.fromEntries(effectCounts),
  };
}

// Expose on window for console access
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as any).__VB_RENDER_DIAG__ = {
    reset: resetDiag,
    stats: getDiagStats,
    enable: () => {
      localStorage.setItem('vb_debug_render', '1');
      console.log('✅ Render diagnostics enabled. Refresh to see logs.');
    },
    disable: () => {
      localStorage.removeItem('vb_debug_render');
      console.log('❌ Render diagnostics disabled. Refresh to stop logs.');
    },
  };
}
